/**
 * Which Google Calendar events this app created itself.
 *
 * ⚠️ WHY THIS IS NOT A LITERAL COMPARISON (CLN-6.7). BookingForm used to filter with
 * `createdBy !== "solerro-site-survey"`. The value on the other side is written by the
 * `send-calendar-invite` Edge Function as:
 *
 *     companyName.toLowerCase().replace(/\s+/g, '-') + '-site-survey'
 *
 * so it is "aurex-site-survey", "joules-site-survey", "quality-solar-site-survey" and so
 * on — one per tenant. Measured 2026-08-19: the literal matched exactly 1 of 12 active
 * tenants.
 *
 * That was not cosmetic. The filtered list feeds `hasCalendarConflict`, which REMOVES
 * time slots from the customer-facing booking form. Excluding the app's own surveys is
 * deliberate: repeat bookings in one slot are governed by `maxBookingsPerSlot`, not by
 * calendar collision. So Solerro's surveys were correctly ignored while every other
 * tenant's own surveys blocked their own slots, and their customers saw fewer times
 * than they should have.
 *
 * Matching the SUFFIX fixes it for every tenant and keeps working for the Solerro
 * events already sitting in Google Calendar, which end in the same suffix.
 */

/** The suffix `send-calendar-invite` appends to a tenant's slug. */
export const SITE_SURVEY_CREATED_BY_SUFFIX = '-site-survey';

export interface CalendarEventLike {
  extendedProperties?: { private?: { createdBy?: string | null } | null } | null;
}

/** True when this app created the event as a site survey, for ANY tenant. */
export function isAppCreatedSiteSurvey(event: CalendarEventLike | null | undefined): boolean {
  const createdBy = event?.extendedProperties?.private?.createdBy;
  return typeof createdBy === 'string' && createdBy.endsWith(SITE_SURVEY_CREATED_BY_SUFFIX);
}

/** Events that should count as real conflicts: everything this app did not create. */
export function excludeAppCreatedSiteSurveys<T extends CalendarEventLike>(events: T[]): T[] {
  return events.filter((e) => !isAppCreatedSiteSurvey(e));
}
